import React, {useCallback} from 'react';

import ColorPicker from '../../../../components/ColorPicker';
import {Analytics} from '../../../../services/analytics';
import {useStatsigGateSemanticLegendsBucketEditing} from '../../../../util/rampFeatureFlags';
import {BucketCustomization} from '../types';
import {NumberRangeInput} from './NumberRangeInput';
import {DEFAULT_COLOR} from './utils/getDefaultBucketColors';

const colorPickerStyle = {marginRight: '6px', marginBottom: '8px'};

export const BucketCustomizationInputWithCategoricalConfig = ({
  bucket,
  bucketMin,
  bucketCustomization,
  index,
  nextBucketMax,
  numberOfBuckets,
  updateBucketColor,
  updateBucketMax,
}: {
  updateBucketColor: (settings: Partial<BucketCustomization>) => void;
  updateBucketMax: (settings: Partial<BucketCustomization>) => void;
  bucket: BucketCustomization;
  bucketMin: number;
  bucketCustomization: BucketCustomization;
  nextBucketMax: number;
  index: number;
  numberOfBuckets: number;
}) => {
  const isBucketEditingEnabled = useStatsigGateSemanticLegendsBucketEditing();

  const bucketColor =
    bucketCustomization.color ?? bucket.color ?? DEFAULT_COLOR;

  const onUpdateColor = useCallback(
    (color: string) => {
      Analytics.track('Semantic legend bucket color changed', {
        newValue: color,
        index,
      });
      updateBucketColor({
        color: color,
      });
    },
    [updateBucketColor, index]
  );

  const onUpdateBucketMax = useCallback(
    (bucketMax: number) => {
      Analytics.track('Semantic legend bucket max changed', {
        newValue: bucketMax,
        index,
      });
      // we don't want to allow the bucket max to be less than the bucket min or greater than the next bucket max
      if (
        bucketMax < bucketMin ||
        (nextBucketMax && bucketMax > nextBucketMax)
      ) {
        return;
      }
      updateBucketMax({
        bucketMax: bucketMax,
      });
    },
    [updateBucketMax, index, bucketMin, nextBucketMax]
  );

  const getMinValue = (
    bucketMin: number,
    index: number,
    bucketCustomization: BucketCustomization
  ) => {
    // if we are at the first bucket, we don't want to show the min value
    return index === 0
      ? undefined
      : (bucketCustomization?.bucketMin ?? bucketMin);
  };

  const getMaxValue = (
    bucketMax: number,
    index: number,
    bucketCustomization: BucketCustomization
  ) => {
    // if we are at the last bucket, we don't want to show the max value
    return index === numberOfBuckets - 1
      ? undefined
      : (bucketCustomization?.bucketMax ?? bucketMax);
  };

  return (
    <div className="flex w-full gap-8" data-test="bucket-customization">
      <NumberRangeInput
        minField="bucketMin"
        maxField="bucketMax"
        minValue={getMinValue(
          bucket.bucketMin ?? 0,
          index,
          bucketCustomization
        )}
        maxValue={getMaxValue(
          bucket.bucketMax ?? 0,
          index,
          bucketCustomization
        )}
        updateSetting={settings => onUpdateBucketMax(settings.bucketMax ?? 0)}
        minDisabled={true} // we never want to allow the min value to be edited
        maxDisabled={!isBucketEditingEnabled} // we never want to allow the max value to be edited
      />
      <ColorPicker
        activeColor={bucketColor ?? ''}
        style={colorPickerStyle}
        setColor={onUpdateColor}
        trigger="button"
      />
    </div>
  );
};
